import { useState, useMemo } from "react";
import { Funnel } from "../services/funnelApi";
import funnelApi from "../services/funnelApi";
import { notification } from "../services/notification";
import FunnelModal from "../components/FunnelModal";

const ITEMS_PER_PAGE = 20;

interface AdminFunnelsProps {
    funnels: Funnel[];
    onFunnelsUpdate: () => void;
}

function AdminFunnels({ funnels, onFunnelsUpdate }: AdminFunnelsProps) {
    const [showModal, setShowModal] = useState(false);
    const [previewHtml, setPreviewHtml] = useState<string | null>(null);
    const [previewTitle, setPreviewTitle] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPageNum, setCurrentPageNum] = useState(1);

    const filteredFunnels = useMemo(() => {
        if (!searchQuery) return funnels;
        const query = searchQuery.toLowerCase();
        return funnels.filter(f => 
            f.title?.toLowerCase().includes(query) ||
            f.slug?.toLowerCase().includes(query) ||
            f.funnelId?.toLowerCase().includes(query)
        );
    }, [funnels, searchQuery]);

    const totalPages = Math.ceil(filteredFunnels.length / ITEMS_PER_PAGE);
    const paginatedFunnels = filteredFunnels.slice(
        (currentPageNum - 1) * ITEMS_PER_PAGE,
        currentPageNum * ITEMS_PER_PAGE
    );

    const handleDelete = async (funnelId: string) => {
        const confirmed = await notification.confirm(
            "Delete Funnel?",
            "Are you sure you want to delete this funnel? This action cannot be undone.",
            "Yes, Delete",
            "Cancel"
        );
        
        if (!confirmed) return;

        try {
            const response = await funnelApi.deleteFunnel(funnelId);
            if (response.success) {
                notification.success("Funnel deleted successfully");
                onFunnelsUpdate();
            } else {
                notification.error(response.error || "Failed to delete funnel");
            }
        } catch (error) {
            notification.error("Failed to delete funnel");
        }
    };

    const handlePreview = (html: string, title: string) => {
        setPreviewHtml(html);
        setPreviewTitle(title);
    };

    const handleClosePreview = () => {
        setPreviewHtml(null);
        setPreviewTitle("");
    };

    const handleOpenFunnel = (funnelId: string, slug: string) => {
        const baseUrl = window.location.origin;
        const url = slug ? `${baseUrl}/funnel/${slug}` : `${baseUrl}/funnel?id=${funnelId}`;
        window.open(url, "_blank");
    };

    const handleFunnelSaved = () => {
        setShowModal(false);
        onFunnelsUpdate();
    };

    return (
        <>
            <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                    <h2 style={{ fontSize: "1.5rem", fontWeight: "700", marginBottom: "4px" }}>Sales Funnels</h2>
                    <p style={{ color: "#6b7280", margin: 0 }}>{filteredFunnels.length} funnels</p>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                    <button onClick={onFunnelsUpdate} style={{ background: "white", color: "#6366f1", padding: "10px 16px", borderRadius: "10px", border: "1px solid #e5e7eb", cursor: "pointer", fontWeight: "600" }}>
                        <i className="fas fa-sync-alt"></i> Refresh
                    </button>
                    <button
                        onClick={() => setShowModal(true)}
                        style={{ background: "linear-gradient(135deg, #6366f1, #8b5cf6)", color: "white", padding: "10px 16px", borderRadius: "10px", border: "none", cursor: "pointer", fontWeight: "600" }}
                    >
                        <i className="fas fa-plus"></i> Generate Funnel
                    </button>
                </div>
            </div>

            <div style={{ background: "white", padding: "16px", borderRadius: "12px", marginBottom: "16px" }}>
                <input 
                    type="text" 
                    placeholder="Search..." 
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setCurrentPageNum(1); }}
                    style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #e5e7eb", boxSizing: "border-box" }}
                />
            </div>

            <div style={{ background: "white", borderRadius: "12px", overflow: "hidden" }}>
                {filteredFunnels.length === 0 ? (
                    <div style={{ padding: "60px", textAlign: "center", color: "#6b7280" }}>No funnels found</div>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "600px" }}>
                            <thead>
                                <tr style={{ background: "#f9fafb" }}>
                                    <th style={{ padding: "12px", textAlign: "left", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>ID</th>
                                    <th style={{ padding: "12px", textAlign: "left", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Title</th>
                                    <th style={{ padding: "12px", textAlign: "left", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Slug</th>
                                    <th style={{ padding: "12px", textAlign: "left", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Created</th>
                                    <th style={{ padding: "12px", textAlign: "center", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedFunnels.map((funnel) => (
                                    <tr key={funnel.funnelId} style={{ borderTop: "1px solid #f3f4f6" }}>
                                        <td style={{ padding: "12px" }}>
                                            <code style={{ background: "#f3f4f6", padding: "4px 8px", borderRadius: "4px", fontSize: "0.75rem" }}>
                                                {funnel.funnelId}
                                            </code>
                                        </td>
                                        <td style={{ padding: "12px", fontWeight: "600", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={funnel.title}>{funnel.title}</td>
                                        <td style={{ padding: "12px" }}>
                                            <span style={{ background: "#eef2ff", color: "#6366f1", padding: "3px 8px", borderRadius: "12px", fontSize: "0.7rem", fontWeight: "500", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block" }} title={funnel.slug}>
                                                {funnel.slug}
                                            </span>
                                        </td>
                                        <td style={{ padding: "12px", fontSize: "0.85rem", color: "#6b7280" }}>
                                            {new Date(funnel.createdDate).toLocaleDateString()}
                                        </td>
                                        <td style={{ padding: "12px", textAlign: "center" }}>
                                            <div style={{ display: "flex", gap: "4px", justifyContent: "center" }}>
                                                <button
                                                    onClick={() => handlePreview(funnel.generatedHtml, funnel.title)}
                                                    title="Preview"
                                                    aria-label="Preview Funnel"
                                                    style={{ background: "#f3f4f6", border: "none", width: "32px", height: "32px", borderRadius: "6px", cursor: "pointer", color: "#6b7280" }}
                                                >
                                                    <i className="fas fa-eye"></i>
                                                </button>
                                                <button
                                                    onClick={() => handleOpenFunnel(funnel.funnelId, funnel.slug)}
                                                    title="Open in new tab"
                                                    aria-label="Open Funnel in new tab"
                                                    style={{ background: "#eef2ff", border: "none", width: "32px", height: "32px", borderRadius: "6px", cursor: "pointer", color: "#6366f1" }}
                                                >
                                                    <i className="fas fa-external-link-alt"></i>
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(funnel.funnelId)}
                                                    title="Delete"
                                                    aria-label="Delete Funnel"
                                                    style={{ background: "#fef2f2", border: "none", width: "32px", height: "32px", borderRadius: "6px", cursor: "pointer", color: "#dc2626" }}
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div style={{ padding: "12px", borderTop: "1px solid #f3f4f6", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "0.8rem", color: "#6b7280" }}>Page {currentPageNum} of {totalPages}</span>
                <div style={{ display: "flex", gap: "6px" }}>
                    <button 
                        aria-label="Prev page"
                        onClick={() => setCurrentPageNum(p => Math.max(1, p - 1))} 
                        disabled={currentPageNum <= 1} 
                        style={{ 
                            padding: "6px 12px", 
                            borderRadius: "6px", 
                            border: "1px solid #e5e7eb", 
                            background: currentPageNum <= 1 ? "#f3f4f6" : "white", 
                            cursor: currentPageNum <= 1 ? "not-allowed" : "pointer",
                            fontSize: "0.85rem" 
                        }}
                    >
                        Prev
                    </button>
                    <button 
                        aria-label="Next page"
                        onClick={() => setCurrentPageNum(p => Math.min(totalPages, p + 1))} 
                        disabled={currentPageNum >= totalPages} 
                        style={{ 
                            padding: "6px 12px", 
                            borderRadius: "6px", 
                            border: "1px solid #e5e7eb", 
                            background: currentPageNum >= totalPages ? "#f3f4f6" : "white", 
                            cursor: currentPageNum >= totalPages ? "not-allowed" : "pointer",
                            fontSize: "0.85rem"
                        }}
                    >
                        Next
                    </button>
                </div>
            </div>

            {showModal && (
                <FunnelModal
                    onClose={() => setShowModal(false)}
                    onSaved={handleFunnelSaved}
                />
            )}

            {previewHtml && (
                <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.8)", zIndex: 9999, display: "flex", flexDirection: "column" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", background: "white", borderBottom: "1px solid #e5e7eb" }}>
                        <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: "600" }}>Preview: {previewTitle}</h3>
                        <button onClick={handleClosePreview} aria-label="Close Preview" style={{ background: "#f3f4f6", border: "none", width: "36px", height: "36px", borderRadius: "8px", cursor: "pointer" }}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                    <iframe srcDoc={previewHtml} style={{ flex: 1, border: "none", width: "100%", background: "white" }} title="Funnel Preview" sandbox="allow-scripts allow-same-origin" />
                </div>
            )}
        </>
    );
}

export default AdminFunnels;
